/** @file Dockview host: registers every panel behind an error boundary, restores or builds the layout. */
import 'dockview-react/dist/styles/dockview.css';
import { DockviewReact, themeDark, type DockviewApi, type DockviewReadyEvent, type IDockviewPanelProps } from 'dockview-react';
import { useEffect, useMemo, useRef, type FunctionComponent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { PanelEntry } from '../panels/panelEntry';
import { ErrorFallback } from '../shell/ErrorFallback';
import { useT } from '../shell/i18n/useT';
import { scheduleLayoutSave } from '../shell/settings/scheduleLayoutSave';
import { settingsStore } from '../stores/settingsStore';
import { applyLayout } from './applyLayout';

function guarded(Panel: FunctionComponent<IDockviewPanelProps>): FunctionComponent<IDockviewPanelProps> {
  return function GuardedPanel(props: IDockviewPanelProps) {
    return (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Panel {...props} />
      </ErrorBoundary>
    );
  };
}

/** The dock area of the editor frame; layout changes are saved (1 s debounce) to settings.ui.layout; a layout reset rebuilds the default. */
export function DockLayout({ panels }: { panels: readonly PanelEntry[] }) {
  const t = useT();
  const dock = useRef<DockviewApi | null>(null);
  const saver = useRef<{ dispose(): void } | null>(null);
  const components = useMemo(() => Object.fromEntries(panels.map((p) => [p.id, guarded(p.component)])), [panels]);
  // "Reset panel layout" saves `layout: null`; while the dock is open that rebuilds the default arrangement.
  useEffect(() => settingsStore.subscribe((state, previous) => {
    if (dock.current === null || state.layout !== null || previous.layout === null) return;
    dock.current.clear();
    applyLayout(dock.current, null, panels, t);
  }), [panels, t]);
  // Stop saving before dockview tears itself down, so an emptied dock is never stored as the layout.
  useEffect(() => () => {
    saver.current?.dispose();
    saver.current = null;
    dock.current = null;
  }, []);
  const onReady = (event: DockviewReadyEvent): void => {
    dock.current = event.api;
    applyLayout(event.api, settingsStore.getState().layout, panels, t);
    saver.current = event.api.onDidLayoutChange(() => { scheduleLayoutSave(event.api.toJSON()); });
  };
  return <DockviewReact className="editor-dock" theme={themeDark} components={components} onReady={onReady} />;
}
